/**
 * @ngdoc type
 * @name mx.SolutionBuilderAgent.SearchQuery
 * @module mx.SolutionBuilderAgent
 *
 * @description
 * An object containing search query information (Search query model)
 * Constructor accepts options as params like:
 * ```javascript
 var options = {
	Id: '4234511B-61D8-43A5-BC2F-2918F68ADD40',
	Title: 'some title',
	Description: 'some description',
	ShowInNavigation: true,
	Public: true,
	System: false,
	Position: 1
 };

 var searchQuery = new SearchQuery(options);
 * ```
 * @property {string} id - Gets or sets search query id. Default: ''
 * @property {string} objectId - Gets or sets an id of an object that contains this search query. Default: ''
 * @property {string} parent - Gets or sets parent query id. Default: ''
 * @property {string} title - Gets or sets search query display name. Default: ''
 * @property {string} description - Gets or sets search query description. Default: ''
 * @property {bool} showInNavigation - Gets or sets whether search query should be displayed as a navigation item in navigation panel. Default: false
 * @property {bool} considerParentFilters - Gets or sets whether search query should include ancestors filter conditions. Default: false
 * @property {bool} public - Gets or sets if search query may be displayed to user
 * @property {bool} system - Gets or sets if search query is delivered "out of the box"
 * @property {int} position - Gets or sets search query display position. Default: 0
 * @property {mx.SolutionBuilderAgent.SearchQueryGroup} group - Gets or sets root {@ref mx.SolutionBuilderAgent.SearchQueryGroup group}. Default: null
 * @property {string} queryFilter - Gets serialized root {@ref mx.SolutionBuilderAgent.SearchQueryGroup group}
 */
angular.module('mx.SolutionBuilderAgent').factory('mx.SolutionBuilderAgent.SearchQuery', searchQueryFactory);
searchQueryFactory.$inject = ['mx.SolutionBuilderAgent.SearchQueryGroup'];
export default function searchQueryFactory(SearchQueryGroup) {
	class SearchQuery {
		constructor(options) {
			options = options || {};
			if (!options.Id) {
				options.CanEdit = true;
			}

			Object.defineProperty(this, 'id', {
				writable: true,
				enumerable: true,
				value: options.Id || '',
			});
			Object.defineProperty(this, 'objectId', {
				writable: true,
				enumerable: true,
				value: options.ObjectId || '',
			});
			Object.defineProperty(this, 'title', {
				writable: true,
				enumerable: true,
				value: options.Title || '',
			});
			Object.defineProperty(this, 'titleNeutral', {
				writable: true,
				enumerable: true,
				value: options.TitleNeutral || '',
			});
			Object.defineProperty(this, 'parent', {
				writable: true,
				enumerable: true,
				value: options.Parent || '',
			});

			Object.defineProperty(this, 'description', {
				writable: true,
				enumerable: true,
				value: options.Description || '',
			});

			Object.defineProperty(this, 'showInNavigation', {
				writable: true,
				enumerable: true,
				value: options.ShowInNavigation === true,
			});

			Object.defineProperty(this, 'considerParentFilters', {
				writable: true,
				enumerable: true,
				value: options.ConsiderParentFilters === true,
			});

			Object.defineProperty(this, 'navigationItem', {
				writable: true,
				enumerable: true,
				value: options.NavigationItem || '',
			});
			Object.defineProperty(this, 'dataQuery', {
				writable: true,
				enumerable: true,
				value: options.DataQuery || '',
			});
			Object.defineProperty(this, 'datasetView', {
				writable: true,
				enumerable: true,
				value: options.DatasetView || '',
			});

			Object.defineProperty(this, 'widgetId', {
				writable: true,
				enumerable: true,
				value: options.Widget || '',
			});

			Object.defineProperty(this, 'system', {
				writable: true,
				enumerable: true,
				value: options.System === true,
			});

			Object.defineProperty(this, 'position', {
				writable: true,
				enumerable: true,
				value: options.Position || 0,
			});

			Object.defineProperty(this, 'quickFilter', {
				writable: true,
				enumerable: true,
				value: options.QuickFilter || 0,
			});

			Object.defineProperty(this, 'canEdit', {
				writable: true,
				enumerable: true,
				value: options.CanEdit || 0,
			});

			const groupOptions = options.QueryFilter ? JSON.parse(options.QueryFilter) : options.Group || null;

			Object.defineProperty(this, 'group', {
				writable: true,
				enumerable: false,
				value: new SearchQueryGroup(groupOptions),
			});

			Object.defineProperty(this, 'queryFilter', {
				enumerable: true,
				get: function () {
					return JSON.stringify(this.group);
				},
			});

			Object.defineProperty(this, '__visible', {
				enumerable: false,
				writable: true,
				value: true,
			});
			Object.defineProperty(this, '__attached', {
				enumerable: false,
				writable: true,
				value: false,
			});
			Object.defineProperty(this, 'parentsChain', {
				enumerable: false,
				value: options.ParentsChain || [],
			});

			Object.defineProperty(this, 'fullTitle', {
				enumerable: false,
				value: this.parentsChain.reduce(function (previousValue, item) {
					return item.Title + ' / ' + previousValue;
				}, this.title),
			});
		}

		/**
		 * @ngdoc method
		 * @name SearchQuery#toSqlView
		 *
		 * @description
		 * Extracts pseudo-SQL expression represented by a search query
		 *
		 * @return {string} Search query pseudo-SQL presentation
		 */
		toSqlView() {
			let queryString;

			if (this.group) {
				queryString = this.group.toSqlView();
			}

			return queryString || '[empty query]';
		}
	}

	return SearchQuery;
}
